/**
 * Learning Center API: groups and requirement profiles (plan
 * docs/plans/lms-plan.md Appendix A "Groups and directory"; 5.3, 8.2, 18.20,
 * 18.21; WP-32's routes, WP-43's client). Typed calls over services/api;
 * nothing under src/lms imports axios (2.3a). Every route is an lms admin
 * route.
 *
 *   GET    /admin/groups?kind=&include_inactive=&limit=&offset=
 *          -> { items: [group + member_count, requirement_count], page }
 *   POST   /admin/groups {name, kind, description?}  -> 201 group
 *          (409 LMS_CONFLICT on a duplicate name, carrying existing_group { id, is_active })
 *   GET    /admin/groups/:id?history=1
 *          -> { group, members, requirements, hidden_member_count, history? }
 *   PATCH  /admin/groups/:id {name?, description?, is_active?} -> { group, reconcile }
 *   DELETE /admin/groups/:id                                    -> { group, reconcile } (is_active false)
 *   POST   /admin/groups/:id/members {user_ids}                 -> { added, already_member, reconcile }
 *   DELETE /admin/groups/:id/members/:userId                    -> { removed, member, reconcile }
 *   POST   /admin/groups/:id/requirements {course_id, due_days} -> 201 requirement + title, slug, course_published, reconcile
 *   DELETE /admin/groups/:id/requirements/:courseId             -> { removed, requirement, reconcile }
 *
 * `reconcile` is the profile reconcile's summary after the change (8.2), or
 * { failed: true, code } when it could not run: the change is written and the
 * nightly pass catches up, so a screen shows it as a warning, never an error
 * (18.20, 18.21).
 */
import api from '../../services/api';
import type { LmsList } from './client';

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Bounds (the server's own, services/lms/groups/validators.js; 5.3, 18.20)
// ---------------------------------------------------------------------------

/** A group name: 1 to 120 characters (18.20). */
export const GROUP_NAME_MAX = 120;
/** A group description: at most 2000 characters (18.20). */
export const GROUP_DESCRIPTION_MAX = 2000;
/** People added in one request (18.20). */
export const MAX_MEMBER_IDS = 500;
/** A requirement's due window in whole days, 0 to 365 (18.20). */
export const REQUIREMENT_DUE_DAYS_MIN = 0;
export const REQUIREMENT_DUE_DAYS_MAX = 365;
/** 5.3: lms_group_requirements.due_days defaults to 30. */
export const DEFAULT_REQUIREMENT_DUE_DAYS = 30;
/** Appendix A: a list page holds at most 200 rows. */
export const GROUPS_MAX_LIMIT = 200;

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** 5.3: a profile carries requirements and assigns them; an adhoc group is a saved audience. */
export type GroupKind = 'profile' | 'adhoc';

/** An lms_groups row (5.3). */
export interface LmsGroup {
  id: string;
  network_id: string;
  name: string;
  kind: GroupKind;
  description: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** One GET /admin/groups item (18.20: the active member and requirement counts). */
export interface GroupListItem extends LmsGroup {
  member_count: number;
  requirement_count: number;
}

/** An active member the admin reaches (G14). */
export interface GroupMember {
  user_id: string;
  full_name: string | null;
  email: string | null;
  added_at: string;
  added_by: string | null;
  /** false for a deactivated or deleted person: the reconcile skips them (18.20). */
  is_active: boolean;
}

/** An active requirement of a profile (18.20). */
export interface GroupRequirement {
  id: string;
  course_id: string;
  title: string | null;
  slug: string | null;
  due_days: number;
  created_at: string;
  created_by: string | null;
  course_is_active: boolean;
  /** false until the course has a published version: nobody is assigned it yet (G8). */
  course_published: boolean;
}

/** A membership row, removed ones included (GET /admin/groups/:id?history=1). */
export interface GroupHistoryRow {
  id: string;
  user_id: string;
  full_name: string | null;
  added_at: string;
  added_by: string | null;
  removed_at: string | null;
  removed_by: string | null;
}

/** GET /admin/groups/:id (Appendix A plus 18.20). */
export interface GroupDetail {
  group: LmsGroup;
  members: GroupMember[];
  requirements: GroupRequirement[];
  /** Members at sites the admin does not reach: counted, never named (G14). */
  hidden_member_count: number;
  history?: GroupHistoryRow[];
}

/** The profile reconcile's summary (services/lms/assignments/reconcile.js). */
export interface ReconcileSummary {
  group_id?: string;
  created: number;
  already_assigned?: number;
  already_current?: number;
  waived?: number;
  skipped_inactive?: number;
  skipped_no_access?: number;
  skipped_courses?: number;
  cancelled: number;
  cancelled_no_access?: number;
  kept_required_elsewhere?: number;
  notifications_failed?: number;
}

/** The reconcile could not run: the change stands, the nightly pass catches up (18.20). */
export interface ReconcileFailed {
  failed: true;
  code: string;
}

/** null when nothing needed reconciling (a rename, a patch that changed nothing). */
export type ReconcileResult = ReconcileSummary | ReconcileFailed | null;

export interface GroupChangeResult {
  group: LmsGroup;
  reconcile: ReconcileResult;
}

export interface AddMembersResult {
  added: number;
  already_member: number;
  reconcile: ReconcileResult;
}

export interface RemoveMemberResult {
  removed: true;
  member: Record<string, unknown>;
  reconcile: ReconcileResult;
}

/** POST /admin/groups/:id/requirements (18.20: the row plus title, slug, course_published, reconcile). */
export interface AddRequirementResult {
  id: string;
  group_id: string;
  course_id: string;
  due_days: number;
  title: string;
  slug: string;
  course_published: boolean;
  reconcile: ReconcileResult;
}

export interface RemoveRequirementResult {
  removed: true;
  requirement: Record<string, unknown>;
  reconcile: ReconcileResult;
}

/** The 409 body of a duplicate name (18.20): the existing group, so the screen can offer to reactivate it. */
export interface ExistingGroupRef {
  id: string;
  is_active: boolean;
}

// ---------------------------------------------------------------------------
// Readers
// ---------------------------------------------------------------------------

/** True when the reconcile after a change could not run (18.20): a warning, not an error. */
export function reconcileFailed(r: ReconcileResult | undefined): r is ReconcileFailed {
  return Boolean(r && (r as ReconcileFailed).failed === true);
}

/** existing_group from a 409 LMS_CONFLICT on a duplicate name, or null (18.20). */
export function existingGroupOf(err: unknown): ExistingGroupRef | null {
  const e = err as { response?: { status?: number; data?: { existing_group?: unknown } } } | null | undefined;
  if (e?.response?.status !== 409) return null;
  const g = e.response.data?.existing_group as { id?: unknown; is_active?: unknown } | undefined;
  if (!g || typeof g.id !== 'string') return null;
  return { id: g.id, is_active: g.is_active !== false };
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export interface GroupListQuery {
  kind?: GroupKind | null;
  includeInactive?: boolean;
  limit?: number;
  offset?: number;
}

/** GET /api/lms/admin/groups (the network's groups by name). */
export async function listGroups(query: GroupListQuery = {}): Promise<LmsList<GroupListItem>> {
  const params: Record<string, string | number> = {};
  if (query.kind) params.kind = query.kind;
  if (query.includeInactive) params.include_inactive = 1;
  if (query.limit !== undefined) params.limit = Math.min(query.limit, GROUPS_MAX_LIMIT);
  if (query.offset !== undefined) params.offset = query.offset;
  const response = await api.get<LmsList<GroupListItem>>('/api/lms/admin/groups', { params });
  return response.data;
}

export interface CreateGroupInput {
  name: string;
  kind: GroupKind;
  description?: string | null;
}

/** POST /api/lms/admin/groups (409 LMS_CONFLICT on a duplicate name; read it with existingGroupOf). */
export async function createGroup(input: CreateGroupInput): Promise<LmsGroup> {
  const body: Record<string, unknown> = { name: input.name, kind: input.kind };
  if (input.description !== undefined) body.description = input.description;
  const response = await api.post<LmsGroup>('/api/lms/admin/groups', body);
  return response.data;
}

/** GET /api/lms/admin/groups/:id (with ?history=1 the membership history too). */
export async function getGroup(groupId: string, options: { history?: boolean } = {}): Promise<GroupDetail> {
  const config = options.history ? { params: { history: 1 } } : undefined;
  const response = await api.get<GroupDetail>(`/api/lms/admin/groups/${enc(groupId)}`, config);
  return response.data;
}

export interface GroupPatch {
  name?: string;
  description?: string | null;
  is_active?: boolean;
}

/** PATCH /api/lms/admin/groups/:id (kind is fixed at creation). */
export async function updateGroup(groupId: string, patch: GroupPatch): Promise<GroupChangeResult> {
  const response = await api.patch<GroupChangeResult>(`/api/lms/admin/groups/${enc(groupId)}`, patch);
  return response.data;
}

/** DELETE /api/lms/admin/groups/:id: is_active false; members and requirements stay as history (8.2). */
export async function deactivateGroup(groupId: string): Promise<GroupChangeResult> {
  const response = await api.delete<GroupChangeResult>(`/api/lms/admin/groups/${enc(groupId)}`);
  return response.data;
}

/** POST /api/lms/admin/groups/:id/members {user_ids} */
export async function addGroupMembers(groupId: string, userIds: string[]): Promise<AddMembersResult> {
  const response = await api.post<AddMembersResult>(`/api/lms/admin/groups/${enc(groupId)}/members`, { user_ids: userIds });
  return response.data;
}

/** DELETE /api/lms/admin/groups/:id/members/:userId (the membership row stays as history). */
export async function removeGroupMember(groupId: string, userId: string): Promise<RemoveMemberResult> {
  const response = await api.delete<RemoveMemberResult>(`/api/lms/admin/groups/${enc(groupId)}/members/${enc(userId)}`);
  return response.data;
}

/** POST /api/lms/admin/groups/:id/requirements {course_id, due_days}: profiles only. */
export async function addGroupRequirement(
  groupId: string,
  input: { course_id: string; due_days: number }
): Promise<AddRequirementResult> {
  const response = await api.post<AddRequirementResult>(`/api/lms/admin/groups/${enc(groupId)}/requirements`, {
    course_id: input.course_id,
    due_days: input.due_days,
  });
  return response.data;
}

/** DELETE /api/lms/admin/groups/:id/requirements/:courseId (remove and add again to change due_days, 18.20). */
export async function removeGroupRequirement(groupId: string, courseId: string): Promise<RemoveRequirementResult> {
  const response = await api.delete<RemoveRequirementResult>(
    `/api/lms/admin/groups/${enc(groupId)}/requirements/${enc(courseId)}`
  );
  return response.data;
}
